import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Loader2 } from 'lucide-react';

interface PDFThumbnailProps {
  url: string;
  width?: number;
}

interface PreviewPosition {
  top: number;
  left: number;
  side: 'left' | 'right';
}

export function PDFThumbnail({ url, width = 64 }: PDFThumbnailProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [Document, setDocument] = useState<any>(null);
  const [Page, setPage] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPos, setPreviewPos] = useState<PreviewPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const previewWidth = 400;
  const previewHeight = previewWidth * 1.3;

  // Dynamically import react-pdf to avoid SSR issues
  useEffect(() => {
    const loadPdf = async () => {
      try {
        const pdfjs = await import('react-pdf');
        // Set up worker
        pdfjs.pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.pdfjs.version}/build/pdf.worker.min.mjs`;
        setDocument(() => pdfjs.Document);
        setPage(() => pdfjs.Page);
      } catch (err) {
        console.error('Failed to load react-pdf:', err);
        setError(true);
      }
    };
    loadPdf();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Determine which side to show preview
        const showOnRight = rect.left < viewportWidth / 2;

        // Calculate horizontal position
        let left: number;
        if (showOnRight) {
          left = rect.right + 16; // 16px gap
        } else {
          left = rect.left - previewWidth - 32; // preview width + padding + gap
        }

        // Calculate vertical position - center on thumbnail, but keep within viewport
        let top = rect.top + rect.height / 2 - previewHeight / 2;

        // Keep preview within viewport bounds
        if (top < 16) top = 16;
        if (top + previewHeight > viewportHeight - 16) {
          top = viewportHeight - previewHeight - 16;
        }

        setPreviewPos({ top, left, side: showOnRight ? 'right' : 'left' });
        setShowPreview(true);
      }
    }, 200);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setShowPreview(false);
    setPreviewPos(null);
  };

  if (error || !Document || !Page) {
    return (
      <div
        className="bg-gray-100 rounded flex items-center justify-center dark:bg-gray-700"
        style={{ width, height: width * 1.25 }}
      >
        <FileText size={24} className="text-gray-400" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Thumbnail */}
      <div
        className="bg-gray-100 rounded overflow-hidden relative cursor-pointer dark:bg-gray-700"
        style={{ width, height: width * 1.25 }}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700">
            <Loader2 size={16} className="animate-spin text-gray-400" />
          </div>
        )}
        <Document
          file={url}
          loading={null}
          onLoadSuccess={() => setLoading(false)}
          onLoadError={() => {
            setLoading(false);
            setError(true);
          }}
          className="flex items-center justify-center"
        >
          <Page
            pageNumber={1}
            width={width}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>

      {/* Hover Preview - rendered via portal to escape overflow:hidden */}
      {showPreview && !loading && !error && previewPos && createPortal(
        <div
          className="fixed z-[9999] bg-white rounded-lg shadow-2xl border border-gray-200 p-2 pointer-events-none dark:bg-gray-800 dark:border-gray-700"
          style={{
            top: previewPos.top,
            left: previewPos.left,
            width: previewWidth + 16,
          }}
        >
          <Document
            file={url}
            loading={
              <div
                className="flex items-center justify-center bg-gray-50 dark:bg-gray-800"
                style={{ width: previewWidth, height: previewHeight }}
              >
                <Loader2 size={32} className="animate-spin text-primary-600 dark:text-primary-400" />
              </div>
            }
          >
            <Page
              pageNumber={1}
              width={previewWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        </div>,
        document.body
      )}
    </div>
  );
}
